import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppState } from "../../context/AppStateContext";
import {
  fetchIssuePoItems,
  fetchIssuePoRegister,
  fetchIssuePoRemainingQtyByItem,
  cancelIssuePo,
  type IssuePoHeader,
  type IssuePoItem,
  type MasterPlanItem,
} from "../../api/mrrInventoryApi";
import { downloadIssuePoPdf } from "../../utils/issuePoPdf";

// ── row status derived from "still to order" balance ─────────────────────────
function deriveStatus(planned: number, stillToOrder: number): "completed" | "partial" | "pending" {
  if (stillToOrder <= 0) return "completed";
  if (stillToOrder < planned) return "partial";
  return "pending";
}

function statusBadge(status: "completed" | "partial" | "pending") {
  if (status === "completed") return <span className="badge badge--complete">Completed</span>;
  if (status === "partial")   return <span className="badge badge--progress">Partial</span>;
  return <span className="badge badge--pending">Pending</span>;
}

function poStatusBadge(status: "completed" | "partial" | "pending") {
  if (status === "completed") return <span className="badge badge--complete">All Completed</span>;
  if (status === "partial")   return <span className="badge badge--progress">Partial</span>;
  return <span className="badge badge--pending">Pending</span>;
}

/**
 * A row shown in the PO register table.
 * - `poQty` is the quantity on this specific PO (null for "unordered" synthetic rows).
 * - `masterPlannedQty` is the master-plan total.
 * - `stillToOrder` is how much of the master plan has NOT yet been put on any PO.
 */
type DisplayRow = {
  key: string;
  standardResourceTitle: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  /** Qty on this specific PO for this beneficiary.  Null for rows that have no PO yet. */
  poQty: number | null;
  masterPlannedQty: number;
  /** master − sum(all PO qtys) = what is still unordered across ALL POs */
  stillToOrder: number;
  derivedStatus: "completed" | "partial" | "pending";
  createdAt: string;
};

type PoGroup = {
  poHeader: IssuePoHeader;
  rows: DisplayRow[];
  poStatus: "completed" | "partial" | "pending";
  /** True for synthetic "Unordered Items" groups (not a real PO). */
  isUnordered?: boolean;
};

export function PoRegisterTab() {
  const { id } = useParams<{ id: string }>();
  const { getById, applications } = useAppState();
  const app = id ? getById(id) : undefined;

  const [poHeaders, setPoHeaders]           = useState<IssuePoHeader[]>([]);
  // poId → IssuePoItem[] (filtered to this beneficiary)
  const [poItemsByPoId, setPoItemsByPoId]   = useState<Map<string, IssuePoItem[]>>(new Map());
  // milestoneTemplateId → remaining qty per item (master − all POs already raised)
  const [remainingMap, setRemainingMap]     = useState<Map<string, Record<string, number>>>(new Map());
  // milestoneTemplateId → master planned qty per item
  const [masterQtyMap, setMasterQtyMap]     = useState<Map<string, Record<string, number>>>(new Map());
  // milestoneTemplateId → all master-plan items (to detect items with no PO yet)
  const [masterItemsMap, setMasterItemsMap] = useState<Map<string, MasterPlanItem[]>>(new Map());

  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [downloadingId, setDownloadingId]   = useState<string | null>(null);
  const [deletingId, setDeletingId]         = useState<string | null>(null);

  async function loadPoRegister() {
    if (!app) return;
    setLoading(true);
    setError(null);
    try {
      const pos = await fetchIssuePoRegister({ limit: 2000 });
      setPoHeaders(pos);

      const milestoneIds = [...new Set(pos.map((p) => p.milestoneTemplateId))].filter(Boolean);

      // Fetch remaining-qty/master-plan and PO items in parallel
      const [milestoneResults, poItemResults] = await Promise.all([
        // Per-milestone master plan + remaining capacity
        Promise.all(
          milestoneIds.map(async (mid) => {
            const res = await fetchIssuePoRemainingQtyByItem({
              milestoneTemplateId: mid,
              applicationIds: [app.id],
            });
            return { mid, res };
          }),
        ),
        // Per-PO line items (one fetch per PO header)
        Promise.all(
          pos.map(async (po) => {
            try {
              const allItems = await fetchIssuePoItems(po.id);
              // Keep only items belonging to this beneficiary
              const appItems = allItems.filter((item) => item.applicationId === app.id);
              return { poId: po.id, items: appItems };
            } catch {
              return { poId: po.id, items: [] as IssuePoItem[] };
            }
          }),
        ),
      ]);

      setRemainingMap(
        new Map(milestoneResults.map(({ mid, res }) => [mid, res.remainingQtyByStandardResourceItemId])),
      );
      setMasterQtyMap(new Map(milestoneResults.map(({ mid, res }) => [mid, res.masterPlannedQtyByStandardResourceItemId ?? {}])));
      setMasterItemsMap(new Map(milestoneResults.map(({ mid, res }) => [mid, res.masterItems ?? []])));
      setPoItemsByPoId(new Map(poItemResults.map(({ poId, items }) => [poId, items])));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load PO register");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPoRegister();
  }, [app?.id]);

  if (!app) return null;

  // ── Build groups: one group per PO, no deduplication ─────────────────────
  const groups: PoGroup[] = [];

  const sortedPos = [...poHeaders].sort((a, b) => {
    const da = a.poDate ?? a.createdAt;
    const db = b.poDate ?? b.createdAt;
    return da < db ? -1 : da > db ? 1 : 0;
  });

  // Track which items have been covered by at least one PO (per milestone)
  const coveredByMilestone = new Map<string, Set<string>>();

  for (const po of sortedPos) {
    const poItems = poItemsByPoId.get(po.id) ?? [];
    if (poItems.length === 0) continue; // No items for this beneficiary on this PO

    const mid = po.milestoneTemplateId;
    if (!coveredByMilestone.has(mid)) coveredByMilestone.set(mid, new Set());

    const rows: DisplayRow[] = poItems.map((item) => {
      const masterQty    = masterQtyMap.get(mid)?.[item.standardResourceItemId] ?? item.plannedQty;
      const remaining    = remainingMap.get(mid)?.[item.standardResourceItemId];
      const stillToOrder = remaining !== undefined ? Math.max(0, remaining) : 0;

      coveredByMilestone.get(mid)!.add(item.standardResourceItemId);

      return {
        key:                  item.id,
        standardResourceTitle: item.standardResourceTitle,
        resourceItemLabel:    item.resourceItemLabel,
        resourceItemUnit:     item.resourceItemUnit,
        poQty:                item.plannedQty,
        masterPlannedQty:     masterQty,
        stillToOrder,
        derivedStatus:        deriveStatus(masterQty, stillToOrder),
        createdAt:            item.createdAt,
      };
    });

    const allDone     = rows.every((r) => r.derivedStatus === "completed");
    const anyProgress = rows.some((r) => r.derivedStatus !== "pending");
    groups.push({
      poHeader: po,
      rows,
      poStatus: allDone ? "completed" : anyProgress ? "partial" : "pending",
    });
  }

  // ── Unordered items: master-plan items with no PO raised yet ──────────────
  // Shown once per milestone as a separate "pending" group tagged isUnordered.
  const processedMilestones = new Set<string>();
  for (const po of sortedPos) {
    const mid = po.milestoneTemplateId;
    if (processedMilestones.has(mid)) continue;
    processedMilestones.add(mid);

    const masterItems = masterItemsMap.get(mid) ?? [];
    const covered     = coveredByMilestone.get(mid) ?? new Set();
    const unordered   = masterItems.filter((mi) => !covered.has(mi.standardResourceItemId));
    if (unordered.length === 0) continue;

    const rows: DisplayRow[] = unordered.map((mi) => {
      const remaining    = remainingMap.get(mid)?.[mi.standardResourceItemId];
      const stillToOrder = remaining !== undefined ? Math.max(0, remaining) : mi.masterPlannedQty;
      return {
        key:                  `unordered-${mid}-${mi.standardResourceItemId}`,
        standardResourceTitle: mi.standardResourceTitle,
        resourceItemLabel:    mi.resourceItemLabel,
        resourceItemUnit:     mi.resourceItemUnit,
        poQty:                null, // no PO raised for this item yet
        masterPlannedQty:     mi.masterPlannedQty,
        stillToOrder,
        derivedStatus:        "pending",
        createdAt:            po.createdAt,
      };
    });

    groups.push({
      poHeader: {
        id:                   `unordered-${mid}`,
        poNumber:             "—",
        milestoneTemplateId:  mid,
        milestoneTitle:       po.milestoneTitle,
        subcontractorMasterId: po.subcontractorMasterId,
        subcontractorFirmName: po.subcontractorFirmName,
        beneficiaryCount:     1,
        selectionMode:        "full",
        notes:                "",
        createdAt:            po.createdAt,
        updatedAt:            po.updatedAt,
      },
      rows,
      poStatus:     "pending",
      isUnordered:  true,
    });
  }

  // ── KPIs — count only real PO groups, not unordered placeholders ──────────
  const realGroups      = groups.filter((g) => !g.isUnordered);
  const totalPos        = realGroups.length;
  const completedPos    = realGroups.filter((g) => g.poStatus === "completed").length;
  const partialPos      = realGroups.filter((g) => g.poStatus === "partial").length;
  const pendingPos      = realGroups.filter((g) => g.poStatus === "pending").length;

  // ── download handler ──────────────────────────────────────────────────────
  async function handleDownload(po: IssuePoHeader) {
    setDownloadingId(po.id);
    try {
      const items: IssuePoItem[] = await fetchIssuePoItems(po.id);
      await downloadIssuePoPdf(po, items, { applications });
    } catch (err) {
      alert(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleCancel(po: IssuePoHeader) {
    if (!po.poNumber || po.poNumber === "—") return;
    const ok = window.confirm(
      `Cancel PO ${po.poNumber}? It will be marked inactive and removed from PO lists. Any issue-material lines created with this PO (not yet issued) will also be removed.`,
    );
    if (!ok) return;
    setDeletingId(po.id);
    try {
      const { removedIssueMaterialRows } = await cancelIssuePo(po.id);
      await loadPoRegister();
      const extra =
        removedIssueMaterialRows > 0 ? ` (${removedIssueMaterialRows} issue material row(s) removed)` : "";
      alert(`PO ${po.poNumber} cancelled${extra}.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Cancel failed");
    } finally {
      setDeletingId(null);
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="tab-body">
      {/* KPI strip */}
      <div className="po-reg__kpis">
        <div className="po-reg__kpi">
          <span className="po-reg__kpi-num">{totalPos}</span>
          <span className="po-reg__kpi-label">Total POs</span>
        </div>
        <div className="po-reg__kpi">
          <span className="po-reg__kpi-num" style={{ color: "#16a34a" }}>{completedPos}</span>
          <span className="po-reg__kpi-label">Completed</span>
        </div>
        <div className="po-reg__kpi">
          <span className="po-reg__kpi-num" style={{ color: "#0891b2" }}>{partialPos}</span>
          <span className="po-reg__kpi-label">Partial</span>
        </div>
        <div className="po-reg__kpi">
          <span className="po-reg__kpi-num" style={{ color: "#d97706" }}>{pendingPos}</span>
          <span className="po-reg__kpi-label">Pending</span>
        </div>
      </div>

      {loading ? (
        <p className="text-muted" style={{ padding: "1.5rem" }}>Loading PO register…</p>
      ) : error ? (
        <div className="card card--elevated" style={{ padding: "1.25rem", color: "#dc2626" }}>
          <strong>Could not load PO register:</strong> {error}
          <p className="text-muted" style={{ marginTop: "0.5rem", fontSize: "0.8125rem" }}>
            Make sure the API server is running and inventory tables are seeded.
          </p>
        </div>
      ) : groups.length === 0 ? (
        <div className="card card--elevated" style={{ padding: "1.5rem" }}>
          <p className="text-muted">No POs have been raised for this beneficiary yet.</p>
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.5rem" }}>
            POs are generated from the Issue Material Register page once a subcontractor and milestone are linked.
          </p>
        </div>
      ) : (
        <div className="card card--flush card--elevated" style={{ marginTop: "1rem" }}>
          {groups.map(({ poHeader, rows, poStatus, isUnordered }) => (
            <div key={poHeader.id} className="po-reg__group">
              <div className="po-reg__group-header">
                <div className="po-reg__group-left">
                  <span className="po-reg__group-title">{poHeader.milestoneTitle}</span>
                  <span className="po-reg__group-sub">{poHeader.subcontractorFirmName}</span>
                </div>
                <div className="po-reg__group-right">
                  {isUnordered ? (
                    <span className="badge badge--pending">Not Ordered</span>
                  ) : (
                    poStatusBadge(poStatus)
                  )}
                  <span className="po-reg__po-number mono">
                    {poHeader.poNumber !== "—"
                      ? `PO: ${poHeader.poNumber}`
                      : isUnordered
                        ? "Items with no PO"
                        : "No PO number"}
                  </span>
                  {poHeader.poDate && (
                    <span className="po-reg__po-date text-muted">{poHeader.poDate}</span>
                  )}
                  <span className="po-reg__group-count">
                    {rows.length} line{rows.length !== 1 ? "s" : ""}
                  </span>
                  {!isUnordered && poHeader.poNumber !== "—" && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={downloadingId === poHeader.id}
                      onClick={() => void handleDownload(poHeader)}
                    >
                      {downloadingId === poHeader.id ? "Generating…" : "↓ Download PO"}
                    </button>
                  )}

                  {!isUnordered && poHeader.poNumber !== "—" && (
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      disabled={deletingId === poHeader.id}
                      onClick={() => void handleCancel(poHeader)}
                      style={{ marginLeft: "0.5rem" }}
                    >
                      {deletingId === poHeader.id ? "Cancelling…" : "Cancel PO"}
                    </button>
                  )}
                </div>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Item</th>
                      <th>Unit</th>
                      <th className="geo-reports__num">This PO Qty</th>
                      <th className="geo-reports__num">Master Planned</th>
                      <th className="geo-reports__num">Still to Order</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td className="text-muted" style={{ fontSize: "0.8125rem" }}>
                          {row.standardResourceTitle}
                        </td>
                        <td style={{ fontWeight: 500 }}>{row.resourceItemLabel}</td>
                        <td className="text-muted">{row.resourceItemUnit}</td>
                        <td className="geo-reports__num" style={{ color: "#0891b2", fontWeight: 600 }}>
                          {row.poQty !== null ? row.poQty : <span className="text-muted">—</span>}
                        </td>
                        <td className="geo-reports__num">{row.masterPlannedQty}</td>
                        <td
                          className="geo-reports__num"
                          style={{ color: row.stillToOrder > 0 ? "#d97706" : "#64748b" }}
                        >
                          {row.stillToOrder}
                        </td>
                        <td>{statusBadge(row.derivedStatus)}</td>
                        <td className="text-muted" style={{ fontSize: "0.8125rem" }}>
                          {row.createdAt.slice(0, 10)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
